import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ShieldCheck } from 'lucide-react';
import Layout from '../components/Layout';
import { Card, cn } from '../components/ui';
import { linkButtonClass } from '../utils/linkButtonClass';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  // サービスについて
  {
    id: 'about-what',
    category: 'サービスについて',
    question: 'otomochiとは何ですか？',
    answer: 'otomochiは、TRPG（テーブルトップロールプレイングゲーム）のセッション録音を高精度でテキスト化する書き起こしサービスです。TRPG特有の用語やダイス目なども正確に認識します。'
  },
  {
    id: 'about-format',
    category: 'サービスについて',
    question: '対応しているファイル形式は？',
    answer: '音声はMP3、WAV、M4A、FLAC形式に、動画はMP4、MOV、M4V、WEBM形式に対応しています。動画をアップロードした場合は、音声を自動で抽出して書き起こします。最大ファイルサイズは2GB（約6時間の音声に相当）です。'
  },
  {
    id: 'about-time',
    category: 'サービスについて',
    question: '処理時間はどのくらいですか？',
    answer: '3時間の録音で約15分程度です。GPUを使用した高速処理により、従来の音声認識サービスより大幅に短縮されています。'
  },
  {
    id: 'about-systems',
    category: 'サービスについて',
    question: 'どのTRPGシステムに対応していますか？',
    answer: 'クトゥルフ神話TRPG、ダンジョンズ＆ドラゴンズ、ソード・ワールド、インセイン、シノビガミなど主要なシステムに対応しています。システム選択により専用辞書が適用され、認識精度が向上します。'
  },
  {
    id: 'about-length',
    category: 'サービスについて',
    question: '1回にアップロードできる録音の長さは？',
    answer: '1回あたりの長さの制限はなく、月間の合計時間の範囲内であれば1回で使い切ることもできます（無料プランは月合計15分、ライトプランは月9時間、スタンダードは月18時間）。ファイルサイズの上限は2GB（約6時間の音声に相当）です。'
  },
  {
    id: 'about-mobile',
    category: 'サービスについて',
    question: 'スマートフォンからも使えますか？',
    answer: 'はい、スマートフォンやタブレットのブラウザからもご利用いただけます。専用アプリのインストールは不要で、録音ファイルのアップロードから書き起こし結果の閲覧まで行えます。'
  },
  {
    id: 'about-publish',
    category: 'サービスについて',
    question: '文字起こしを同人誌等やサイトに公開してもいいですか？',
    answer: `otomochiを利用して文字起こしを行ったということがわかるように、キャプションに入れていただければ問題ありません。

【キャプション例】
・文字起こし：otomochi
・本ログはotomochiを使用して文字起こしを行いました。
・Transcribed by otomochi

ただログを公開する際には、シナリオの権利関係、同卓PL等の確認は必ずしてください。

利用規約にあるとおり、音声ファイル等の入力・提供したすべての情報に関する、所有権、知的財産権その他一切の権利は、すべて利用者に帰属します。また、書き起こし結果についても利用者に諸権利が帰属し、サービス提供者はその権利を主張しません。`
  },
  // 料金について
  {
    id: 'price-free',
    category: '料金について',
    question: '無料で使えますか？',
    answer: 'はい、無料プランでは月3回・合計15分までの書き起こしが可能です。本格的にご利用いただく場合は有料プランをご検討ください。'
  },
  {
    id: 'price-oneshot',
    category: '料金について',
    question: 'ワンショット課金とは何ですか？',
    answer: `必要な分だけ、時間や書き起こし回数を買い足せる都度課金です。

・時間：1時間120円。サブスクリプションを契約していなくても購入でき、月額プランとの併用も可能です（無制限プランを除く）。購入した時間は月額プランの月間枠とは別に、使い切るまで保持されます（課金期間をまたいでも失われません）。

・回数：1回100円。ライト・スタンダードプランをご契約中の方が購入できます。購入した回数は無期限で、月の回数を使い切ったあとに1回ずつ使われます。`
  },
  {
    id: 'price-sessions-used-up',
    category: '料金について',
    question: '月の書き起こし回数を使い切ったらどうなりますか？',
    answer: `購入済みの回数があれば、自動的に1回ずつ使われ、そのまま書き起こせます。購入済みの回数がない場合は、月の時間が残っていても、その月はそれ以上書き起こせなくなります（回数は次の更新日にリセットされます）。続けて書き起こしたい場合は、次の方法があります。

・ライト・スタンダードプラン：回数を1回100円で追加購入できます。購入した回数は無期限で、月の回数を使い切ったあとに1回ずつ使われます。

・無料プラン：回数の追加購入はできません。有料プランへの変更をご検討ください。

なお、月の時間を使い切ったあと、追加時間（購入した時間や特典コードで増えた時間）で書き起こす場合は、回数の上限はかかりません。`
  },
  {
    id: 'price-standard-vs-unlimited',
    category: '料金について',
    question: 'スタンダードプランと無制限プランの違いは？',
    answer: `スタンダードプラン（月¥1,000）は月6回・合計18時間まで書き起こしでき、話者分離や話者名の編集に対応しています。

無制限プラン（月¥4,000）は書き起こしの回数・時間の制限がありません。さらに、書き起こし結果の保持期間が7日間に延長され（他プランは完了から8時間）、処理後の音声も聞き直し用に7日間だけ保持されます。結果画面での音声の再生機能も無制限プラン限定です。たくさん書き起こしたい方や、あとから音声で確認・修正したい方に向いています。`
  },
  {
    id: 'price-change',
    category: '料金について',
    question: 'プランの変更はできますか？',
    answer: `はい、いつでもプラン変更が可能です。変更の向きによって適用タイミングが異なります。

・アップグレード（上位プランへ）：その場ですぐに適用され、利用枠の上限が新しいプランに引き上がります。残り期間の差額が日割りで請求され、その月に使った時間はそのまま引き継がれます（上限だけが上がり、使用済みの時間はリセットされません）。

・ダウングレード（下位プランへ）：次回更新日から適用され、それまでは現在のプランのままご利用いただけます。

なお、月間の残り時間が翌月や新しいプランへ繰り越されることはありません。`
  },
  {
    id: 'price-cancel',
    category: '料金について',
    question: '解約はできますか？',
    answer: 'はい、いつでも解約可能です。解約後も契約期間終了まではサービスをご利用いただけます。'
  },
  // プライバシー・セキュリティ
  {
    id: 'privacy-external-ai',
    category: 'プライバシー・セキュリティ',
    question: 'AIって、ChatGPTやGeminiのこと？シナリオや音声が外部に漏れませんか？',
    answer: `いいえ。otomochi は ChatGPT（OpenAI）や Gemini（Google）などの外部の生成AIサービスを利用していません。

音声をテキスト化するAI（Faster-Whisper）も、固有名詞などの表記ゆれを整える補正AI（オープンソースの大規模言語モデル）も、いずれも otomochi が自分で保持・管理するモデルを、当サービスの管理下で動かしています。あなたのシナリオ・音声・書き起こしテキストを、ChatGPT や Gemini のような外部のAIサービスへ送ったり共有したりすることはありません。

さらに、お預かりしたデータをAIの学習・再学習に使うことも一切ありません。処理は「書き起こし」と「表記の補正」の目的だけに使い、処理に使った音声は処理後に自動削除されます（無制限プランのみ、聞き直し用に7日間だけ保持し、本人だけがアクセスできます）。`
  },
  {
    id: 'privacy-audio',
    category: 'プライバシー・セキュリティ',
    question: 'アップロードした音声ファイルはどうなりますか？',
    answer: '音声ファイルは処理完了後に自動削除されます。ただし 無制限プランのみ、結果画面でセグメントごとに音声を聞き直せるよう、処理済みの音声を結果と同じ保持期限（7日間）までサーバ上に保持し、本人のみがアクセスできます。保持期限を過ぎると音声は結果とともに自動削除されます。それ以外のプランでは音声を再生目的で保持しません。'
  },
  {
    id: 'privacy-result',
    category: 'プライバシー・セキュリティ',
    question: '書き起こし結果はいつまで保存されますか？',
    answer: '書き起こし結果は完了から8時間後に自動削除されます。無制限プランのみ、音声の聞き直し・修正を行えるよう保持期限を7日間に延長しています。いずれの場合も、必要な場合は早めにダウンロードしてください。'
  },
  {
    id: 'privacy-payment',
    category: 'プライバシー・セキュリティ',
    question: '決済情報は安全ですか？',
    answer: 'はい、決済処理はStripeを通じて行われます。クレジットカード情報は当サービスのサーバーには保存されません。'
  },
  // 技術的な質問
  {
    id: 'tech-diarization',
    category: '技術的な質問',
    question: '話者分離機能とは何ですか？',
    answer: '音声から複数の話者を自動識別し、GMとプレイヤーを区別して表示する機能です。誰が何を話したかが一目でわかります。話者分離はスタンダードプラン・無制限プランでご利用いただけます。'
  },
  {
    id: 'tech-speaker-name',
    category: '技術的な質問',
    question: '話者名は編集できますか？',
    answer: '書き起こし結果の画面で、自動識別された「話者1」「話者2」などのラベルを、GMやキャラクター名など実際の名前に付け替えできます。変更は同じ話者のすべての発話・ダウンロード出力にまとめて反映されます。話者名の編集はスタンダードプラン・無制限プランでご利用いただけます。'
  },
  {
    id: 'tech-timestamp',
    category: '技術的な質問',
    question: 'タイムスタンプはどのように表示されますか？',
    answer: '各発話の開始時間が表示されます。後から特定のシーンを探したい時に便利です。'
  },
  {
    id: 'tech-output',
    category: '技術的な質問',
    question: '出力形式は何がありますか？',
    answer: 'TXT（テキスト）、JSON、HTML形式で出力可能です。用途に合わせてお選びください。'
  }
];

const categoryOrder = [...new Set(faqData.map((item) => item.category))];

export default function FAQPage() {
  // 開閉状態は「項目ID」で管理する（カテゴリ切替でインデックスがずれて
  // 無関係な項目が開いて見える不具合を防ぐ）。
  const [openId, setOpenId] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const visibleCategories =
    activeCategory === 'all' ? categoryOrder : [activeCategory];

  const filters: { key: string; label: string }[] = [
    { key: 'all', label: 'すべて' },
    ...categoryOrder.map((c) => ({ key: c, label: c })),
  ];

  return (
    <Layout showPromo>
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link
            to="/"
            className="inline-flex items-center text-primary hover:text-primary-hover transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            トップに戻る
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-text-heading mb-3">よくある質問</h1>
          <p className="text-text-sub">otomochiに関するよくある質問をまとめました</p>
        </div>

        {/* プライバシーの安心バナー（不安を先回りで解消） */}
        <div className="mb-10 flex items-start gap-3 rounded-panel border border-accent/30 bg-accent/5 px-5 py-4">
          <ShieldCheck className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-text-heading">
              ChatGPTやGeminiなどの外部AIには送りません
            </p>
            <p className="text-sm text-text-sub mt-1 leading-relaxed">
              音声・テキストは otomochi 自身が管理するモデルだけで処理し、学習にも使いません。処理後の音声は自動削除されます。
              <button
                type="button"
                onClick={() => {
                  setActiveCategory('all');
                  setOpenId('privacy-external-ai');
                  document
                    .getElementById('faq-privacy-external-ai')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                className="ml-1 text-accent hover:text-accent-hover font-medium underline underline-offset-2"
              >
                詳しく
              </button>
            </p>
            <p className="text-sm text-text-sub mt-1 leading-relaxed">
              データの扱いの全体像は
              <Link
                to="/terms-simple"
                className="mx-1 text-accent hover:text-accent-hover font-medium underline underline-offset-2"
              >
                かんたんな説明
              </Link>
              にまとめています。
            </p>
          </div>
        </div>

        {/* カテゴリーフィルター */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {filters.map((f) => {
            const active = activeCategory === f.key;
            return (
              <button
                key={f.key}
                type="button"
                aria-pressed={active}
                onClick={() => setActiveCategory(f.key)}
                className={cn(
                  'rounded-control px-4 py-2 text-sm font-medium transition-colors',
                  active
                    ? 'bg-primary text-white'
                    : 'bg-card text-text-sub shadow-card hover:text-primary'
                )}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* FAQ（カテゴリ見出し付き） */}
        <div className="space-y-10">
          {visibleCategories.map((category) => {
            const items = faqData.filter((item) => item.category === category);
            return (
              <section key={category}>
                <h2 className="mb-3 px-1 text-sm font-bold uppercase tracking-wide text-primary">
                  {category}
                </h2>
                <div className="space-y-3">
                  {items.map((item) => {
                    const isOpen = openId === item.id;
                    return (
                      <Card
                        key={item.id}
                        id={`faq-${item.id}`}
                        padding="none"
                        className={cn(
                          'overflow-hidden transition-shadow',
                          isOpen && 'ring-1 ring-primary/30'
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => setOpenId(isOpen ? null : item.id)}
                          aria-expanded={isOpen}
                          aria-controls={`faq-panel-${item.id}`}
                          className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-background"
                        >
                          <h3 className="text-base font-bold text-text-heading leading-snug">
                            {item.question}
                          </h3>
                          <ChevronDown
                            className={cn(
                              'w-5 h-5 flex-shrink-0 text-primary transition-transform duration-300',
                              isOpen && 'rotate-180'
                            )}
                          />
                        </button>
                        {isOpen && (
                          <div id={`faq-panel-${item.id}`} role="region">
                            <p className="px-6 pb-5 text-text-sub leading-relaxed whitespace-pre-line">
                              {item.answer}
                            </p>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        {/* お問い合わせへの誘導 */}
        <Card className="mt-12 text-center p-8">
          <h2 className="text-xl font-bold text-text-heading mb-4">
            お探しの質問が見つかりませんか？
          </h2>
          <p className="text-text-sub mb-6">お気軽にお問い合わせください</p>
          <Link to="/contact" className={linkButtonClass('primary', 'inline-flex')}>
            お問い合わせ
          </Link>
        </Card>
      </main>
    </Layout>
  );
}
